import { useState } from "react";
import { ChevronDown, ChevronRight, Copy, Edit, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Insight } from "@/services/api/InsightService";
import { LeadInfoField } from "@/types/leads/lead-info";
import { InsightForm } from "./InsightForm";

interface InsightListProps {
  insights: Insight[];
  onSelect?: (insight: Insight) => void;
  onUpdate: (
    id: number,
    updates: { name?: string; description?: string; fields?: LeadInfoField[]; is_default?: boolean },
  ) => Promise<void>;
  onDelete: (insightId: number) => void;
  onDuplicate: (insight: Insight) => void;
}

export function InsightList({ insights, onSelect, onUpdate, onDelete, onDuplicate }: InsightListProps) {
  const [deletingInsightId, setDeletingInsightId] = useState<number | null>(null);
  const [expandedFields, setExpandedFields] = useState<Set<number>>(new Set());
  const [editingInsightId, setEditingInsightId] = useState<number | null>(null);

  const handleDelete = (insightId: number) => {
    setDeletingInsightId(insightId);
    onDelete(insightId);
  };

  return (
    <div className="grid gap-4 overflow-y-auto brand-scroll pr-2">
      {insights.map((insight) => (
        <Card key={insight.id}>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="flex-1">
                <CardTitle className="text-base">{insight.name}</CardTitle>
                {!editingInsightId && insight.description && <p className="text-sm text-gray-600 mt-1">{insight.description}</p>}
                {insight.is_default && (
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mt-2">Default</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                {onSelect && editingInsightId !== insight.id && (
                  <Button type="button" size="sm" variant="outline" onClick={() => onSelect(insight)} className="text-xs">
                    Select
                  </Button>
                )}
                {editingInsightId !== insight.id && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onDuplicate(insight)}
                    title="Duplicate insight"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setEditingInsightId(editingInsightId === insight.id ? null : insight.id)}
                  className={editingInsightId === insight.id ? "text-gray-500" : ""}
                >
                  {editingInsightId === insight.id ? "Cancel" : <Edit className="h-4 w-4" />}
                </Button>
                {editingInsightId !== insight.id && !insight.is_default && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(insight.id)}
                    disabled={deletingInsightId !== null}
                  >
                    {deletingInsightId === insight.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {editingInsightId === insight.id ? (
              <InsightForm
                insight={insight}
                onSubmit={async (data) => {
                  await onUpdate(insight.id, data);
                  setEditingInsightId(null);
                }}
                onCancel={() => setEditingInsightId(null)}
              />
            ) : (
              <>
                <div className="mb-3">
                  <button
                    type="button"
                    className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5 hover:text-gray-900"
                    onClick={() =>
                      setExpandedFields((prev) => {
                        const next = new Set(prev);
                        next.has(insight.id) ? next.delete(insight.id) : next.add(insight.id);
                        return next;
                      })
                    }
                  >
                    {expandedFields.has(insight.id) ? (
                      <ChevronDown className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5" />
                    )}
                    Fields
                    <span className="bg-gray-100 text-gray-500 text-xs px-1.5 py-0.5 rounded-full font-normal">
                      {(insight.fields || []).length}
                    </span>
                  </button>
                  {expandedFields.has(insight.id) &&
                    ((insight.fields || []).length > 0 ? (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                        {(insight.fields || []).map((f) => (
                          <span
                            key={f.key}
                            className="bg-gray-100 text-gray-700 text-xs px-2 py-1.5 rounded-md border border-gray-200 break-words"
                          >
                            {f.label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">No fields configured</span>
                    ))}
                </div>

                <div>
                  <div className="text-sm flex items-center gap-1.5 font-medium text-gray-700 mb-1.5">
                    Connected Genies
                    <span className="bg-gray-100 text-gray-500 text-xs px-1.5 py-0.5 rounded-full font-normal">
                      {insight.dependent_agents?.length || 0}
                    </span>
                  </div>
                  {insight.dependent_agents && insight.dependent_agents.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {insight.dependent_agents.map((agent) => (
                        <span
                          key={agent.id}
                          className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-md border border-gray-200"
                        >
                          {agent.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
